import { refreshToken } from '@/shared/authentication/services/oidc';
import Logger from '@/shared/logger';

import { getInsuranceUrl } from '../host-urls';

import { makeBaseQuery } from './baseQuery';

/**
 * Builds a host's base query wrapped to recover from expired-token 401s. On a 401 it
 * runs a single OIDC silent renew via `refreshToken` (which dedupes concurrent calls —
 * across instances too — and signs the user out on failure) and, if a fresh token comes
 * back, retries the request once.
 *
 * Closes the 401 gap the legacy axios stack handled but RTK Query did not.
 */
export const makeBaseQueryWithReauth = (
  getBaseUrl: () => string | undefined
): ReturnType<typeof makeBaseQuery> => {
  const baseQuery = makeBaseQuery(getBaseUrl);

  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    // A 401 with a non-JSON body (proxy/gateway error page) surfaces as
    // PARSING_ERROR with the real status in originalStatus — still a 401.
    const error = result.error;
    const effectiveStatus =
      error?.status === 'PARSING_ERROR' ? error.originalStatus : error?.status;

    if (effectiveStatus === 401) {
      try {
        // Dedup'd across concurrent 401s; resolves to a User on success, or null after
        // `refreshToken` has already triggered sign-out on a dead session.
        const user = await refreshToken();
        if (user?.access_token) {
          result = await baseQuery(args, api, extraOptions); // retry once with the renewed token
        }
      } catch (refreshError) {
        // A failed refresh must degrade to the original 401 result so endpoints see a
        // normal auth error instead of an unhandled throw out of the base query.
        Logger.trace('Token refresh threw during 401 recovery', {
          level: 'Error',
          tag: 'baseQueryWithReauth',
          sentryLogging: true,
          // baseUrl tells the three host instances apart in Sentry — the tag alone can't
          extra: { error: refreshError, baseUrl: getBaseUrl() }
        });
      }
    }

    return result;
  };
};

const baseQueryWithReauth = makeBaseQueryWithReauth(getInsuranceUrl);

export default baseQueryWithReauth;
